import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from './material/material.module';
import { SideNavComponent } from './side-nav/side-nav.component';
import { ToolbarComponent } from './side-nav/toolbar/toolbar.component';
import { NguCarouselModule } from '@ngu/carousel';
import { RoutingModule } from './routing/routing.module';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { SharedModule } from './utility-packages/shared/shared.module';
import { ToastrModule } from 'ngx-toastr';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { AppConfig } from './config-service/app.config';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { environment } from 'src/environments/environment';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { RouterState, StoreRouterConnectingModule } from '@ngrx/router-store';
import { reducers, metaReducers } from './reducers';
import { EntityDataModule, EntityDataService, EntityDefinitionService, EntityMetadataMap } from '@ngrx/data';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { HomeModule } from './home/home.module';
import { RouterModule } from '@angular/router';
import { IAnnounceList } from './home/home-announcements/models/announcements.model';
import { AnnouncementsDataService } from './home/home-announcements/ngrx/announcements-data.service';
import { CardOperationsComponent } from './card-operations/card-operations.component';
import { FaqModalComponent } from './card-operations/faq-modal/faq-modal.component';
import { DatePickerComponent } from './card-operations/components/date-picker/date-picker.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as fromHome from '../app/home/ngrx/reducers'
import { HomeEffects } from './home/ngrx/home-effects';
import { VersionHelper } from './utility-packages/versionHelper/version-helper';
import { CardPaymentComponent } from './card-operations/components/online-charge-components/card-payment/card-payment.component';
import { CardSaveComponent } from './card-operations-core/components/card-save/card-save.component';
import { CreditCardSelectionComponent } from './card-operations-core/components/credit-card-selection/credit-card-selection.component';
import { PayWithCreditCardComponent } from './card-operations-core/components/pay-with-credit-card/pay-with-credit-card.component';
import { PayWithDefinedCardCoreComponent } from './card-operations-core/components/pay-with-defined-card/pay-with-defined-card.component';
import { PaymentComponent } from './card-operations-core/components/payment/payment.component';
import { CreditCardSelectorDirective } from './card-operations-core/directives/credit-card-selector.directive';
import { es3dgateService } from './card-operations-core/services/3dsgate/3dsRequest.service';
import { ProductService } from './card-operations/services/getProducts.service';
import { CardTransactionService } from './card-operations/services/getCardTransactions.service';
import { TransactionRequestService } from './card-operations/services/transactionRequest.service';
import { Service3dsRequest } from './card-operations/services/3dsRequest.service';
import { TransactionCompleteService } from './card-operations/services/transactionComplete.service';
import { EtisalatComponent } from './card-operations-core/components/etisalat/etisalat.component';
import { FirstStepComponent } from './card-operations/components/online-charge-components/firs-step/firs-step.component';
import { SecondStepComponent } from './card-operations/components/online-charge-components/second-step/second-step.component';
import { PaymentResultsEtisalatComponent } from './card-operations-core/high-order-components/payment-results-etisalat/payment-results-etisalat.component';
import { TransactionsComponent } from './card-operations/components/online-charge-components/transactions/transactions.component';
import { CardDetailComponent } from './card-operations/components/card-detail/card-detail.component';
import { QueryAndFavComponent } from './card-operations/components/query-and-fav/query-and-fav.component';

export function initializeApp(appConfig: AppConfig) {
  return () => appConfig.load();
}

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient);
}

const entityMetadata:EntityMetadataMap = {
  Announcements: {
    selectId: (announcements:IAnnounceList) => announcements.id
  }
}

@NgModule({
  declarations: [
    AppComponent,
    SideNavComponent,
    ToolbarComponent,
    CardOperationsComponent,
    FaqModalComponent,
    DatePickerComponent,
    CardPaymentComponent,
    TransactionsComponent,
    CardSaveComponent,
    CreditCardSelectionComponent,
    PayWithCreditCardComponent,
    PayWithDefinedCardCoreComponent,
    PaymentComponent,
    CreditCardSelectorDirective,
    EtisalatComponent,
    FirstStepComponent,
    SecondStepComponent,
    PaymentResultsEtisalatComponent,
    CardDetailComponent,
    QueryAndFavComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    EffectsModule.forRoot([HomeEffects]),
    StoreModule.forRoot(reducers, { metaReducers }),
    StoreModule.forFeature("home", fromHome.homeReducers),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
    StoreRouterConnectingModule.forRoot({
      stateKey: 'router',
      routerState: RouterState.Minimal,
    }),
    EntityDataModule.forRoot({}),
    MaterialModule,
    NguCarouselModule,
    RoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    SharedModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      closeButton: true
    }),
    NgbModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  exports: [CardOperationsComponent],
  providers: [
    CookieService,
    AppConfig,
    { provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppConfig], multi: true 
    },
    VersionHelper,
    es3dgateService,
    ProductService,
    CardTransactionService,
    TransactionRequestService,
    Service3dsRequest,
    TransactionCompleteService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(    
    private eds: EntityDefinitionService,
    private entityDataService: EntityDataService,
    private announcementsDataService: AnnouncementsDataService
  ){
    eds.registerMetadataMap(entityMetadata);
    entityDataService.registerService('Announcements', announcementsDataService);
  }
}
